import PropTypes from 'prop-types'
import React, { useCallback, useState } from 'react'
import _concat from 'lodash/concat'
import _join from 'lodash/join'
import _isFunction from 'lodash/isFunction'
import { connect as connectRedux } from 'react-redux'
import compose from 'recompose/compose'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { List, Map, fromJS } from 'immutable'
import { connect as connectStaticText } from '../StaticText/connect'
import { getBoundActions } from '../../actions/index'
import Button, { TYPES } from '../Button/index'
import { ICON_TYPES } from '../Icon.v2/index'
import TextSeasonEpisode from '../TextSeasonEpisode/index'
import {
  NotificationsFollowButton,
  BUTTON_TYPES,
} from '../NotificationsFollowButton/index'
import { SUBSCRIPTION_TYPES } from '../../services/notifications/index'
import { TYPE_SHARE_V2_SHARE } from '../../services/dialog/index'
import { isEntitled } from '../../services/subscription/index'
import PlaylistAddRemove from '../PlaylistAddRemove/index'
import { formatDuration } from '../../theme/web-app/index'
import TitleVideoLink, { TYPE as VIDEO_LINK_TYPE } from '../TitleVideoLink/index'
import { getAuthIsLoggedIn } from '../../services/auth/index'
import { SHARE_INITIATED_GA4_EVENT } from '../../services/event-tracking/index'
import { VOTE_SIZE_MEDIUM } from '../../services/recommend/index'
import VideoPlayerVote from '../VideoPlayerVote/index'
import { selectOracleGuideOnSideClosed, selectOracleGuideEnabled } from '../../services/oracle/selectors'

function closeShareDialog (props) {
  const { clearShare } = props
  clearShare()
}

function getClassName (props) {
  const className = _concat(['video-player-meta'], props.className || [])
  if (props.visible) {
    className.push('video-player-meta--visible')
  }
  return _join(className, ' ')
}

function VideoPlayerMeta (props) {
  const {
    seriesId,
    title,
    season,
    episode,
    isShareable,
    staticText,
    playerPlay,
    playerPause,
    isPlaying,
    auth,
    id,
    upstreamContext,
    duration,
    clearUpstreamContext,
    refreshComments,
    comments,
    video,
    videoPlayerInfoDisabled,
    isGuide,
    series,
    contentType,
    mediaId,
    mediaLang,
    votesVisible,
    setEventDefault,
    guideOnSideClosed,
    guideEnabled,
    setOracleGuideOnSideClosed,
    primaryCourse,
  } = props
  const [mobileVisibility, setMobileVisibility] = useState('__invisible')
  const userIsEntitled = isEntitled(auth.get('subscriptions', List()))
  const isLoggedIn = getAuthIsLoggedIn(auth)
  const isFullscreen = document.fullscreenElement
    || document.mozFullScreenElement
    || document.webkitFullscreenElement

  let ga4EventData = {
    content_name: title,
    content_id: id,
    content_type: video.getIn(['data', 'contentType']),
    ui_location: 'Video Player',
  }
  if (seriesId) {
    ga4EventData = {
      ...ga4EventData,
      parent_series_name: series,
      parent_series_id: seriesId,
    }
  }

  const renderShareModal = useCallback(() => {
    const wasPlaying = isPlaying()
    playerPause()
    props.renderModal(TYPE_SHARE_V2_SHARE, {
      onDismiss: () => {
        if (wasPlaying) playerPlay()
        closeShareDialog(props)
      },
      contentId: props.id,
      contentUrl: video.getIn(['data', 'path']),
      contentType: video.getIn(['data', 'contentType']),
      parentSeriesId: seriesId,
      parentSeriesName: series,
      enableTime: true,
      title: props.title,
      uiLocation: 'Video Player',
    })

    setEventDefault(SHARE_INITIATED_GA4_EVENT.mergeDeep({
      ...ga4EventData,
    }))
  }, [])

  const onCommentsClicked = () => {
    if (_isFunction(playerPause)) {
      playerPause()
    }
    clearUpstreamContext()
    refreshComments({
      contentId: id,
      commentsId: comments.getIn(['data', 'id']),
      metadata: video.get('data', Map()).toJS(),
      jwt: auth.get('jwt'),
    })
  }

  // v1: Simple open/close behavior
  const onDiscoverMoreClicked = () => {
    setOracleGuideOnSideClosed(false) // Open the guide
  }

  const showCommentsButton = isLoggedIn && userIsEntitled && !primaryCourse
  const showFollowButton = seriesId && !primaryCourse

  return (
    <div
      className={`${getClassName(props)}`}
    >
      {!videoPlayerInfoDisabled
        ? <div className={'expandable'}>
          <TitleVideoLink
            type={VIDEO_LINK_TYPE.VIDEO}
            titleClass={'video-player-meta__title'}
            hasHeader
          />
          <div className="expandable__content">
            <TitleVideoLink
              type={primaryCourse ? VIDEO_LINK_TYPE.COURSE : VIDEO_LINK_TYPE.SERIES}
              titleClass={'video-player-meta__series-title'}
              primaryCourse={primaryCourse}
              hasHeader
            />
            <div className="video-player-meta__meta-details">
              {!primaryCourse && (
                <TextSeasonEpisode
                  className={['video-player-meta__season-episode']}
                  season={season}
                  episode={episode}
                  isGuide={isGuide}
                />
              )}
              <span className="video-player-meta__duration">
                {formatDuration(duration)}
              </span>
            </div>
            {isLoggedIn && userIsEntitled ? (
              <Button
                type={TYPES.ICON}
                icon={ICON_TYPES.ELLIPSIS}
                onClick={() => { setMobileVisibility('') }}
                className="video-player-meta__action-button mobile-button-menu"
                stacked
                inverted
              />
            ) : null}
            <div className="video-player-meta__actions-container ">
              {isLoggedIn && userIsEntitled && <PlaylistAddRemove
                upstreamContext={upstreamContext}
                contentId={id}
                contentTitle={title}
                circlularIcon
                ga4EventData={{
                  content_id: id,
                  content_type: contentType,
                  content_name: title,
                  media_id: mediaId,
                  media_language: mediaLang,
                  is_featured_media: true,
                  parent_series_name: series,
                  parent_series_id: seriesId,
                  ui_element: 'Video Player',
                }}
              />
              }
              {showFollowButton ? (
                <NotificationsFollowButton
                  className="video-player-meta__action-button"
                  subscriptionType={SUBSCRIPTION_TYPES.SERIES}
                  type={BUTTON_TYPES.LINK}
                  contentId={seriesId}
                  uiLocation={'Video Player'}
                />
              ) : null}
              {isShareable && !isFullscreen && userIsEntitled && isLoggedIn ? (
                <Button
                  type={TYPES.ICON}
                  icon={ICON_TYPES.CIRCULAR_SHARE}
                  onClick={renderShareModal}
                  className="video-player-meta__action-button button--with-icon"
                  stacked
                  inverted
                >
                  {staticText.getIn(['data', 'share'])}
                </Button>
              ) : null}
              {showCommentsButton ? (
                <Button
                  type={TYPES.ICON}
                  icon={ICON_TYPES.CIRCULAR_COMMENTS}
                  onClick={onCommentsClicked}
                  className="video-player-meta__action-button button--with-icon"
                  stacked
                  inverted
                >
                  {staticText.getIn(['data', 'comments'])}
                </Button>
              ) : null}
              {/* v1: Always render when guide is enabled; fade in/out via CSS */}
              {guideEnabled ? (
                <Button
                  type={TYPES.ICON}
                  icon={ICON_TYPES.SHERPA}
                  onClick={onDiscoverMoreClicked}
                  className={`video-player-meta__action-button button--with-icon video-player-meta__ai-guide-button${guideOnSideClosed ? ' video-player-meta__ai-guide-button--visible' : ''}`}
                  stacked
                  inverted
                >
                  {staticText.getIn(['data', 'diveDeeperWithAiGuide'])}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
        : null
      }
      <div className={`overlay-mobile${mobileVisibility}`}>
        <div className="options-container">
          <Button
            type={TYPES.ICON}
            icon={ICON_TYPES.CHEVRON_LEFT}
            onClick={() => { setMobileVisibility('__invisible') }}
            className="video-player-meta__action-button button--with-icon"
            stacked
            inverted
          >
            {staticText.getIn(['data', 'options'])}
          </Button>
          <Button
            type={TYPES.ICON}
            icon={ICON_TYPES.CLOSE}
            onClick={() => { setMobileVisibility('__invisible') }}
            className="video-player-meta__action-button button--with-icon"
            stacked
            inverted
          />
        </div>
        <div className="links-container">
          {isLoggedIn && <PlaylistAddRemove
            upstreamContext={upstreamContext}
            contentId={id}
            contentTitle={title}
            circlularIcon
            ga4EventData={{
              content_id: id,
              content_type: contentType,
              content_name: title,
              media_id: mediaId,
              media_language: mediaLang,
              is_featured_media: true,
              parent_series_name: series,
              parent_series_id: seriesId,
              ui_element: 'Video Player',
            }}
          />
          }
          {showFollowButton ? (
            <NotificationsFollowButton
              className="video-player-meta__action-button"
              subscriptionType={SUBSCRIPTION_TYPES.SERIES}
              type={BUTTON_TYPES.LINK}
              contentId={seriesId}
              uiLocation={'Video Player'}
            />
          ) : null}
          {isShareable && !isFullscreen && userIsEntitled && isLoggedIn ? (
            <div>
              <Button
                type={TYPES.ICON}
                icon={ICON_TYPES.CIRCULAR_SHARE}
                onClick={renderShareModal}
                className="video-player-meta__action-button video-player-meta__action-button--mobile button--with-icon"
                stacked
                inverted
              >
                {staticText.getIn(['data', 'share'])}
              </Button>
            </div>
          ) : null}
          {showCommentsButton ? (
            <div>
              <Button
                type={TYPES.ICON}
                icon={ICON_TYPES.CIRCULAR_COMMENTS}
                onClick={onCommentsClicked}
                className="video-player-meta__action-button video-player-meta__action-button--mobile button--with-icon"
                stacked
                inverted
              >
                {staticText.getIn(['data', 'comments'])}
              </Button>
            </div>
          ) : null}
          {/* v1: Always render when guide is enabled; fade in/out via CSS */}
          {guideEnabled ? (
            <div>
              <Button
                type={TYPES.ICON}
                icon={ICON_TYPES.SHERPA}
                onClick={onDiscoverMoreClicked}
                className={`video-player-meta__action-button video-player-meta__action-button--mobile button--with-icon video-player-meta__ai-guide-button${guideOnSideClosed ? ' video-player-meta__ai-guide-button--visible' : ''}`}
                stacked
                inverted
              >
                {staticText.getIn(['data', 'diveDeeperWithAiGuide'])}
              </Button>
            </div>
          ) : null}
        </div>
      </div>
      {userIsEntitled && isLoggedIn && votesVisible && !videoPlayerInfoDisabled
        && <div className="video-player-meta__vote">
          <p className="video-player-meta__vote-title">
            {staticText.getIn(['data', 'voteTitle'])}
          </p>
          <VideoPlayerVote
            auth={auth}
            contentId={id}
            size={VOTE_SIZE_MEDIUM}
            heartText={staticText.getIn(['data', 'voteLoveLabel'])}
            thumbUpText={staticText.getIn(['data', 'voteUpLabel'])}
            thumbDownText={staticText.getIn(['data', 'voteDownLabel'])}
            ga4EventData={fromJS(ga4EventData)}
          />
          <p className="video-player-meta__vote-hint">
            {staticText.getIn(['data', 'voteHint'])}
          </p>
        </div>
      }
    </div>
  )
}

VideoPlayerMeta.propTypes = {
  id: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  series: PropTypes.string,
  seriesId: PropTypes.number,
  mediaId: PropTypes.number,
  host: PropTypes.string,
  season: PropTypes.number,
  episode: PropTypes.number,
  contentType: PropTypes.string,
  description: PropTypes.string,
  geoAvailibility: PropTypes.string.isRequired,
  visible: PropTypes.bool,
  moreVisible: PropTypes.bool,
  votesVisible: PropTypes.bool,
  className: PropTypes.array,
  toggleMoreVisible: PropTypes.func,
  setAutoHide: PropTypes.func,
  playerPause: PropTypes.func.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  isGuide: PropTypes.bool,
  guideOnSideClosed: PropTypes.bool,
  guideEnabled: PropTypes.bool,
  setOracleGuideOnSideClosed: PropTypes.func,
  primaryCourse: ImmutablePropTypes.map,
}

export default compose(
  connectStaticText({ storeKey: 'videoPlayerMeta' }),
  connectRedux(
    (state) => {
      const {
        media, auth, comments, video, featureTracking, upstreamContext,
      } = state
      return {
        isShareable: media.getIn(['data', 'isShareable'], false),
        auth,
        comments,
        video,
        videoPlayerInfoDisabled: featureTracking.getIn(
          ['data', 'disableVideoInfo'],
          false,
        ),
        guideOnSideClosed: selectOracleGuideOnSideClosed(state),
        guideEnabled: selectOracleGuideEnabled(state),
        upstreamContext,
      }
    },
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        renderModal: actions.dialog.renderModal,
        clearShare: actions.share.clearShare,
        clearUpstreamContext: actions.upstreamContext.clearUpstreamContext,
        refreshComments: actions.comments.refreshComments,
        setFeatureTrackingDataPersistent:
          actions.featureTracking.setFeatureTrackingDataPersistent,
        setEventDefault: actions.eventTracking.setEventDefault,
        setOracleGuideOnSideClosed: actions.oracle.setOracleGuideOnSideClosed,
      }
    },
  ),
)(VideoPlayerMeta)
